<template>
  <div class="h-64">
    <ChartLineDate
      id="revenueSummaryChart"
      :datasets="datasets"
      :labels="labels"
      period="month"
      granularity="month"
      :y-axes-formatter="
        dataType === 'cardTurnover' ? 'currency' : 'number'
      "
      :currency="currency"
      @hover-datapoint="onHoverDatapoint"
    />
  </div>
  <div
    class="flex shrink-0 items-center space-x-4 rounded-lg bg-background-fg/5 p-4"
  >
    <template v-if="hoveredData">
      <template
        v-for="dataset in hoveredData.datasets"
        :key="dataset.color"
      >
        <ColorItem :value="dataset.color" rounded />
        <div class="flex w-full flex-wrap justify-between">
          <div class="flex space-x-4 pr-4">
            <span>
              {{ dataset.label }}
            </span>
            <p class="text-center font-semibold">
              {{
                dataset.label === "Card Turnover"
                  ? formatToCurrencyString(
                      Number(dataset.value),
                      currency,
                      {
                        compact: false,
                        decimals: false,
                        inputInMinorUnits: true,
                      }
                    )
                  : formatNumberWithCommas(dataset.value)
              }}
            </p>
          </div>
          <div
            class="flex items-center text-sm text-background-fg/70"
          >
            {{
              buildFormattedLabel(
                hoveredData.label,
                hoveredData.index === undefined
                  ? "range"
                  : "week"
              )
            }}
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import type {
  HoveredDataPoint,
  ChartLineDateDataset,
} from "@/components/ChartLineDate.vue"

import type { MerchantCurrencyCode } from "@/utils/currency"
import type { WeeklyTransactionChartData } from "@/services/reports/retrieveWeeklyTransactionChartData"
import type { WeeklyTransactionSummaryType } from "./WeeklyTransactionSummaryTypeToggle.vue"

const props = defineProps<{
  data: WeeklyTransactionChartData[]
  currency: MerchantCurrencyCode
  dataType: WeeklyTransactionSummaryType
}>()

const primary = useCssVar("--color-primary")

const datasets = computed<Array<ChartLineDateDataset>>(
  () => [
    props.dataType === "cardTurnover"
      ? {
          label: "Card Turnover",
          data: props.data.map(({ revenue }) => revenue),
          color: `rgb(${primary.value})`,
          overview: "total",
        }
      : props.dataType === "activeMerchantCount"
        ? {
            label: "Active Merchants",
            data: props.data.map(
              ({ clientsTransacting }) => clientsTransacting
            ),
            color: `rgb(${primary.value})`,
            overview: "maximum",
          }
        : {
            label: "Transactions",
            data: props.data.map(
              ({ transactionCount }) => transactionCount
            ),
            color: `rgb(${primary.value})`,
            overview: "maximum",
          },
  ]
)

const labels = computed(() =>
  props.data.map(({ weekStart }) => {
    const weekStartDate = DateFns.parse(
      weekStart,
      "yyyy-MM-dd",
      new Date()
    )
    return {
      start: DateFns.startOfISOWeek(weekStartDate),
      end: DateFns.endOfISOWeek(weekStartDate),
    }
  })
)

const onHoverDatapoint = (
  hoveredData_: HoveredDataPoint
) => {
  hoveredData.value = hoveredData_
}

const hoveredData = ref<HoveredDataPoint>()

const buildFormattedLabel = (
  { start, end }: { start: Date; end: Date },
  granularity: "hour" | "day" | "week" | "month" | "range"
) => {
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return "No data"
  }
  return granularity === "hour"
    ? `${formatToTime(start)} - ${formatToTime(end)}`
    : granularity === "day"
      ? formatToDate(start)
      : `${DateFns.format(
          start,
          "EEE do MMM yy"
        )} - ${DateFns.format(end, "EEE do MMM yy")}`
}
</script>

<style scoped>
.chart {
  height: 400px;
}
</style>
